import { motion } from "framer-motion";
import { ArrowRight, FileCheck2 } from "lucide-react";
import { scrollToSection } from "@/lib/lenis";
import heroPort from "@/assets/hero_port.jpg";

const HERO_IMAGE = heroPort;

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
};

const line = {
  hidden: { y: "110%", opacity: 0 },
  show: {
    y: "0%",
    opacity: 1,
    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
  },
};

export const Hero = () => {
  const scrollTo = (href) => scrollToSection(href);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end sm:items-center overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A140F] via-[#0A140F]/80 to-[#0A140F]/45" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A140F]/70 via-transparent to-[#0A140F]/40" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-10 pb-16 sm:pb-0 pt-32 sm:pt-0 w-full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="hero-badge"
          className="inline-flex items-center gap-2 label-eyebrow text-gold border border-gold/35 rounded-full px-4 py-1.5 mb-6 bg-[#0A140F]/40"
        >
          <FileCheck2 size={13} />
          30 Years of Excellence &bull; Est. 1996
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-4xl"
        >
          <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#F4F7F5]">
            {["Moving Cargo.", "Clearing Borders.", "30 Years of Getting It Done."].map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span variants={line} className="block">
                  {i === 2 ? <span className="gold-gradient-text">{l}</span> : l}
                </motion.span>
              </span>
            ))}
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          data-testid="hero-subheadline"
          className="mt-6 max-w-xl text-base sm:text-lg text-[#D7E0DA] leading-relaxed"
        >
          Jamaica's most trusted customs broker and freight forwarder, connecting the Caribbean
          with North America, Asia, South America, and Africa.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-request-quote-cta"
            onClick={() => scrollTo("#quote")}
            className="group bg-gold hover:bg-gold-hover text-[#0A140F] font-display font-semibold text-sm sm:text-base px-7 py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors"
          >
            Request a Freight Quote
            <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            data-testid="hero-start-filing-cta"
            onClick={() => scrollTo("#customs-filing")}
            className="border border-gold/60 text-gold-light hover:bg-gold/10 font-display font-semibold text-sm sm:text-base px-7 py-3.5 rounded-full transition-colors"
          >
            Start a Customs Filing
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          data-testid="hero-stats-bar"
          className="mt-12 sm:mt-16 flex flex-wrap gap-x-8 gap-y-3 border-t border-gold/20 pt-6 max-w-2xl"
        >
          {[
            ["30+", "Years Licensed"],
            ["99.8%", "Compliance Rate"],
            ["24–48hr", "Port Clearance"],
            ["Kingston", "Headquarters"],
          ].map(([num, label]) => (
            <div key={label}>
              <div className="font-display font-bold text-xl sm:text-2xl text-gold">{num}</div>
              <div className="text-xs text-[#A1B0A6] uppercase tracking-wide">{label}</div>
            </div>
          ))}
        </motion.div>

        <p
          data-testid="hero-stats-disclaimer"
          className="mt-3 text-[11px] text-[#6E7E74]"
        >
          *Conditions apply. Clearance times vary by cargo type, trade lane, and documentation readiness.
        </p>
      </div>
    </section>
  );
};
